import { generatePath } from "react-router";

import type { Branch, BranchId } from "~/branching/model/branch";
import { resolveBranchIdForRoute } from "~/branching/model/resolve-default-branch";
import { routes } from "~/routes";

export type SystemHomeTab =
  | "index"
  | "directory"
  | "workflows"
  | "branches"
  | "settings"
  | "commits"
  | "change-requests"
  | "change-request-detail"
  | "create-change-request";

export type ParsedSystemHomeRoute = {
  desiredBranchId: BranchId | null;
  tab: SystemHomeTab;
  nodeId: string | null;
  changeRequestId: string | null;
};

export type BuildSystemHomeRouteScope = {
  systemId: string;
  branchId?: BranchId | null;
  tab?: SystemHomeTab;
  nodeId?: string | null;
  changeRequestId?: string | null;
};

export function buildStableSystemRoute(systemId: string): string {
  return `${routes.Systems}/${encodeURIComponent(systemId)}`;
}

export function buildResolvedSystemDetailPath(scope: { systemId: string; systemRedesign: boolean }): string {
  if (scope.systemRedesign) return buildSystemHomeRoute({ systemId: scope.systemId });
  return generatePath(routes.SystemDetails, { systemId: scope.systemId, "*": "" });
}

export function buildSystemHomeRoute(scope: BuildSystemHomeRouteScope): string {
  const tab = scope.tab ?? "index";
  const base = `${routes.SystemsDashboard}/${scope.systemId}`;
  const branchSegment = scope.branchId != null ? `/bid/${encodeURIComponent(scope.branchId)}` : "";

  switch (tab) {
    case "index":
      return `${base}${branchSegment}`;
    case "directory": {
      const directoryPath = `${base}${branchSegment}/directory`;
      return scope.nodeId ? `${directoryPath}/${encodeURIComponent(scope.nodeId)}` : directoryPath;
    }
    case "workflows":
      return `${base}${branchSegment}/workflows`;
    case "branches":
      return `${base}${branchSegment}/branches`;
    case "settings":
      return `${base}${branchSegment}/settings`;
    case "commits":
      return `${base}${branchSegment}/commits`;
    case "change-requests":
      return `${base}${branchSegment}/change-requests`;
    case "change-request-detail":
      if (!scope.changeRequestId) {
        return `${base}${branchSegment}/change-requests`;
      }
      return `${base}${branchSegment}/change-requests/${encodeURIComponent(scope.changeRequestId)}`;
    case "create-change-request":
      return `${base}${branchSegment}/change-requests/new`;
  }
}

function buildLegacySubsystemSnapshotHref(
  subsystem: { id: string; taggedConfiguration: { id: string } },
  snapshotId: string,
): string {
  return `/systems/${subsystem.id}/${subsystem.taggedConfiguration.id}/${snapshotId}`;
}

export function buildLegacySubsystemDetailsHref(subsystem: {
  id: string;
  taggedConfiguration: { id: string };
  taggedSnapshotId: string;
}): string {
  return buildLegacySubsystemSnapshotHref(subsystem, subsystem.taggedSnapshotId);
}

export function buildAllCommitsHref(systemId: string, selectedBranch: Branch | null, branches: Branch[]): string {
  const branchId = selectedBranch ? resolveBranchIdForRoute(selectedBranch, branches) : null;
  return buildSystemHomeRoute({ systemId, branchId, tab: "commits" });
}
